import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CardSurface, Notice, Skeleton } from '@/components';
import { SignInPanel, type SignInMode } from '@/components/auth/SignInPanel';
import {
  AuthError,
  authErrorMessageKey,
  MIN_PASSWORD_LENGTH,
  signInWithEmail,
  signUpWithEmail,
} from '@/lib/auth';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { queryKeys } from '@/lib/query-keys';
import { safeNextPath, useSession } from '@/lib/session';

/**
 * `/sign-in` — `FE-06.1`, `FR-PERM-001`, `entry-surfaces.md` D7/D12.
 *
 * The composition site for `SignInPanel`: the panel is presentational and
 * this file owns every hook it needs — the session probe, the two Better
 * Auth calls, the `['me']` refetch and the router.
 *
 * ---------------------------------------------------------------------------
 * THE FIRST ROUTE IN THIS REPOSITORY WITH SEARCH PARAMS — THE PRECEDENT
 * ---------------------------------------------------------------------------
 * `validateSearch` is a **total function**, not a bare schema, and the shape
 * below is what the routes that follow (`FE-23.1`'s `?state=open|closed`,
 * `FR-SEARCH-012`'s board filter) should copy:
 *
 *  1. **Zod, declared once, and the type derived with `z.infer`** — never both
 *     written by hand. That is `packages/shared`'s rule and there is no reason
 *     the client's own URL contracts should follow a different one.
 *  2. **`safeParse`, never `parse`.** `validateSearch` runs during *matching*,
 *     so a throw there is not a form error — it is the whole route failing to
 *     match, and the member lands in `RouteError` because somebody sent them
 *     `/sign-in?next[]=x`. A querystring is attacker-controlled input on every
 *     route that has one; degrading a malformed value to "absent" is the only
 *     behaviour that cannot be weaponised into an error page.
 *  3. **The validated value is still not trusted.** `next` is a `string` after
 *     validation and that says nothing about where it points —
 *     `safeNextPath` (brief §5.2) is what makes it safe to navigate to, and it
 *     is applied at every read, not at validation time. Zod can prove the
 *     type; only `safeNextPath` can prove the *origin*.
 *  4. **Unknown keys are stripped from the validated OUTPUT** (Zod's
 *     `z.object` default) — but ⚠ **not from the match's search**. The router
 *     merges the validator's output *over* the raw parsed querystring
 *     (`router.js:688`, measured), so validation adds and corrects but never
 *     removes. A route must therefore treat its own search type as a
 *     convenience, not a runtime guarantee, for any key it rejects. See the
 *     narrowing in `SignInRoute` below, which is where that bites.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE FOUR STATES (D12), AND WHY "pending" IS NOT `pendingComponent`
 * ---------------------------------------------------------------------------
 * The brief's §6.7 table says the pending state is "the route's
 * `pendingComponent`". **It cannot be, and this is a measured correction.**
 * TanStack Router resolves `pendingComponent` for a route's own *loader*, and
 * this route has none — the session is a `useQuery`, not route data — so a
 * `pendingComponent` here would be dead code and the form would flash for a
 * signed-in member on every visit, which is the one defect D12 exists to
 * prevent. The pending state is therefore `useSession().isPending`, branched
 * on inside the component, and it renders the sheet's own skeleton: a title
 * bar, two field rows and a button row, in the panel's own geometry, so the
 * page does not resize when the form replaces it.
 *
 * A loader was considered and rejected for the same reason `routes/b.…tsx`
 * rejected one: a loader's pending state is the shell-shaped `RoutePending`,
 * and D12 asks for a sheet-shaped one.
 *
 * ---------------------------------------------------------------------------
 * ⚠ `isSignedOut` WINS OVER `profile`, AND THE ORDER IS THE DECISION
 * ---------------------------------------------------------------------------
 * `useSession()` can return a **non-null `profile` beside `isSignedOut: true`**
 * — TanStack Query keeps the last successful `data` alongside a new error, so
 * an expired session is exactly that shape. Every branch in this chunk tests
 * `isSignedOut` first. Here that means an expired session renders the form
 * rather than bouncing to `next`, which is right: the bounce would land on a
 * page whose own queries 401 and send the member straight back here.
 *
 * ---------------------------------------------------------------------------
 * D11 — THIS ROUTE ANNOUNCES NOTHING
 * ---------------------------------------------------------------------------
 * `SignInPanel` calls `announce()` itself for the mode change, and a sign-in
 * failure is a `Notice tone="bad"` (`role="alert"`) which announces itself.
 * A `useEffect` here watching `mode` would be the double-read D11 forbids and
 * the brief names as the likeliest defect in this chunk. There is deliberately
 * no `announce` import in this file.
 */

/**
 * `next` is optional and nothing else is accepted. There is no second key
 * here on purpose: `mode` was considered (a `?mode=create-account` deep link
 * into the create-account half) and rejected — `FR-PERM-001` names no such
 * entry point, the toggle is one click away, and a search param is a contract
 * every later reader has to keep.
 */
const signInSearchSchema = z.object({
  next: z.string().optional(),
});

type SignInSearch = z.infer<typeof signInSearchSchema>;

export const Route = createFileRoute('/sign-in')({
  validateSearch: (search: Record<string, unknown>): SignInSearch => {
    const parsed = signInSearchSchema.safeParse(search);
    // Malformed ⇒ absent, never a throw. See the precedent note above.
    return parsed.success ? parsed.data : {};
  },
  component: SignInRoute,
});

/**
 * Is self-registration open in this build?
 *
 * ---------------------------------------------------------------------------
 * ⚠ `import.meta.env.PROD`, NOT A `VITE_*` VARIABLE, AND THE REASON IS THE
 * DEPLOYMENT
 * ---------------------------------------------------------------------------
 * The production `web` service runs `vite preview` over a bundle that was
 * built inside the Docker image (`Dockerfile`, the `build` stage). Vite inlines
 * `import.meta.env.VITE_*` at **build** time, so a `VITE_SIGNUP_ENABLED` added
 * to `compose.prod.yaml`'s `environment:` block would be read long after the
 * only moment it could have had an effect — a silent no-op, and the sort that
 * looks configured. `PROD` needs nothing configured: it is `true` in the image's
 * bundle and `false` under `vite dev`, which is what `playwright.config.ts`
 * starts and what `pnpm dev` serves.
 *
 * ⚠ `pnpm --filter @molt/web build` ON THIS MACHINE DOES NOT REPRODUCE THE
 * DEPLOYED BUNDLE, and the difference is exactly this constant. Vite reads
 * `NODE_ENV` from a `.env` file when it resolves `import.meta.env.PROD`, and
 * `.env.example` — the file `CLAUDE.md`'s bootstrap tells you to copy — ships
 * `NODE_ENV=development`. So a local build emits `SIGN_UP_OPEN = true` (and a
 * `jsxDEV` bundle besides), while the image emits `false`. Measured on both,
 * by reading the minified chunk. The image is right because `Dockerfile`'s
 * build stage now states `ENV NODE_ENV=production`; before that it was right
 * only because `.dockerignore` happens to exclude `.env`. To reproduce the
 * deployed bundle locally, build with `NODE_ENV=production` in front —
 * inspecting `apps/web/dist` after a plain `pnpm build` will tell you
 * registration is open when it is not.
 *
 * That axis is deliberately *not* the server's. `apps/server`'s
 * `emailAndPassword.disableSignUp` is the enforcement and reads
 * `SIGNUP_ENABLED`/`NODE_ENV`; this constant only decides whether a button is
 * drawn. Hiding it closes nothing on its own, and re-opening the server key
 * without rebuilding the web image re-opens the route without the button. Both
 * default to the same thing — closed in production, open everywhere else — so
 * they only diverge when somebody states one and not the other.
 *
 * Development and test keep registration open on purpose: roughly thirty
 * Playwright specs mint their actors through `POST /api/auth/sign-up/email`,
 * and `FR-PERM-001` drives this very toggle in a browser.
 */
const SIGN_UP_OPEN = !import.meta.env.PROD;

/**
 * The sheet's own skeleton (D12), not `RoutePending`'s three generic rows:
 * the point of a shaped skeleton is that the page does not jump when the real
 * content arrives, and a centred `max-w-sm` sheet is not the shape of a page
 * of rows. `Skeleton` is `aria-hidden` by construction; `aria-busy` on the
 * wrapper is the silent signal that something is mid-update — this route
 * announces nothing (see the file docstring).
 */
function SignInPending() {
  return (
    <div aria-busy="true" className="mx-auto flex w-full max-w-sm flex-col gap-4">
      <CardSurface variant="sheet" className="flex flex-col gap-6 p-6">
        <Skeleton shape="line" width="8rem" />
        <Skeleton shape="row" count={2} />
        <Skeleton shape="row" />
      </CardSurface>
    </div>
  );
}

/**
 * `FE-80.1` — the rendered failure sentence, re-resolved from the CODE through
 * the interface-language catalogue.
 *
 * `AuthError.message` is still the mapped English sentence `lib/auth.ts`
 * produced for a caller with no translator in hand; it is deliberately not what
 * this route renders. Baking the sentence at throw time — inside a module that
 * cannot reach the context — would make a served translation unreachable for
 * ever, which is the "declared, pinned, never rendered" defect FE-80.1 keeps
 * finding. So the route holds the translator and the code, and asks for the key.
 *
 * ⚠ `{ count }` is passed for EVERY key, not only the one that has a slot.
 * `interfaceMessage` ignores an unused value and THROWS on a missing one, so
 * passing it unconditionally makes the throw unreachable. The alternative — a
 * per-key values argument — puts a runtime throw one careless edit away.
 */
function authSentence(
  t: (key: string, values?: Record<string, string | number>, count?: number) => string,
  thrown: unknown,
): string {
  if (!(thrown instanceof AuthError)) return t('signedOut.error.generic');
  const key = authErrorMessageKey(thrown.code);
  /*
   * ⚠ THE SERVERTEXT ARM. `INVALID_DISPLAY_NAME` is this repository's own
   * `databaseHooks` check and its sentence is authored in
   * `apps/server/src/auth/better-auth.ts`, arriving in the response body. A
   * catalogue cannot translate a response body, so there is no key to resolve
   * and the server's own wording passes through verbatim. This branch is the
   * one place a reader can see that, and it is not an oversight.
   */
  if (key === null) return thrown.serverMessage ?? t('signedOut.error.generic');
  return t(key, { count: MIN_PASSWORD_LENGTH });
}

function SignInRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile, isPending, isSignedOut, error } = useSession();
  // ⚠ Above every early return (`isPending`, `hasSession`) — a context read
  // below one of them is a conditional hook.
  const { t } = useInterfaceLanguage();

  const [mode, setMode] = useState<SignInMode>('sign-in');
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  /*
   * ⚠ **`validateSearch` cannot STRIP a key it rejected, and this was
   * measured.** `@tanstack/router-core@1.171.20`, `router.js:688`, builds a
   * match's search as `{ ...parentSearch, ...strictSearch }` — the raw parsed
   * querystring spread FIRST, the validator's output merged over it. So a key
   * the validator omits survives from the raw search, and `useSearch()` hands
   * it back. `?next=123` is the case: `defaultParseSearch` is
   * `parseSearchWith(JSON.parse)`, so `next` arrives as the **number** 123,
   * Zod drops it, and it comes back anyway.
   *
   * The type says `string | undefined`; the runtime does not guarantee it. So
   * `next` is narrowed here rather than trusted, and every consumer below
   * reads the narrowed value. `safeNextPath` would have caught it regardless
   * (its first line is `typeof next !== 'string'`), which is exactly why that
   * guard is written as a type check and not as a prefix check — but the
   * *banner* would not have: `search.next !== undefined` is true for `123`,
   * and a member who typed a junk URL would be told "Sign in to continue"
   * with nothing to continue to.
   *
   * `redirected` is therefore derived from the resolved destination, not from
   * the presence of the parameter. `signInHref` never emits `next=/`, so a
   * `nextPath` of `/` means "nothing usable arrived" — which covers the junk
   * value, the open-redirect attempt and the absent key with one test.
   */
  const nextParam = typeof search.next === 'string' ? search.next : undefined;
  const nextPath = safeNextPath(nextParam);
  const redirected = nextPath !== '/';
  // `isSignedOut` first — see the file docstring. A session that has expired
  // carries its last-good profile beside the 401, and treating that as "signed
  // in" would redirect the member to a page that immediately 401s.
  const hasSession = !isSignedOut && profile !== null;

  useEffect(() => {
    if (!hasSession) return;
    /*
     * `href`, not `to`. `nextPath` is an arbitrary same-origin path that may
     * carry a query string (`/b/aB3xY9z/renovation?filter=overdue`), and `to`
     * is matched against the route-path union — a query string inside it is
     * not parsed and would be dropped. `navigate({ href })` parses the string
     * into `to` + `search` + `hash` before building the location
     * (`@tanstack/router-core@1.171.20`, `router.js` `buildAndCommitLocation`),
     * which is the only form that preserves the querystring `FR-PERM-001`'s
     * round trip is asserted on.
     *
     * `replace: true` so Back does not walk into a page that bounces again.
     */
    void navigate({ href: nextPath, replace: true });
  }, [hasSession, nextPath, navigate]);

  async function submit(credentials: { email: string; password: string; name: string }) {
    setBusy(true);
    setErrorMessage(undefined);
    try {
      if (mode === 'create-account') {
        await signUpWithEmail(credentials);
      } else {
        await signInWithEmail({
          email: credentials.email,
          password: credentials.password,
        });
      }

      /*
       * ⚠ §5.4: refetch `['me']` BEFORE navigating. Better Auth's 200 body is
       * `{ token, user }` in its own field names and carries no `username`,
       * `initials` or `initialsColour` — those are derived by our server — so
       * it must not be written into the cache. Navigating first works too, and
       * renders the signed-out header for a frame while the shell's probe
       * catches up: a visible flash, and one the screenshot pass would catch.
       *
       * `invalidateQueries` rather than `fetchQuery`: the shell's `useSession`
       * observer is mounted on every route including this one, so the
       * invalidation refetches through the options that observer already
       * holds, and `queryKeys.me()` stays the single spelling of the key.
       */
      await queryClient.invalidateQueries({ queryKey: queryKeys.me() });
      await navigate({ href: nextPath, replace: true });
    } catch (thrown) {
      /*
       * ⚠ `FE-80.1` corrected this comment along with the code: this route DOES
       * now map a code — it is the only file on the path that holds a
       * translator (`authSentence` above, brief §5.2). The panel still maps
       * none: `SignInPanelProps.errorMessage` is handed an already
       * member-facing sentence, exactly as before. Anything that is not an
       * `AuthError` is a programming error rather than a server answer, and
       * gets the same generic sentence the adapter's own default row uses —
       * through the same key, so one translation covers both.
       */
      setErrorMessage(authSentence(t, thrown));
    } finally {
      setBusy(false);
    }
  }

  if (isPending) return <SignInPending />;

  /*
   * There is a session and the effect above is navigating away. Render the
   * skeleton rather than the form (D12: an already-signed-in member must
   * never see the form flash) and rather than `null` (a blank page for a
   * frame reads as a broken route, and the skeleton is already the honest
   * "something is happening" rendering this route owns).
   */
  if (hasSession) return <SignInPending />;

  /*
   * "Any other error" (D12's fourth row): the probe failed for a reason that
   * is not `unauthenticated` — `internal`, a dropped connection. Show the form
   * anyway, because the member may still be able to sign in and refusing a
   * form because a *session probe* failed is the worse outcome.
   *
   * Suppressed once a submit has failed: that `Notice` is `role="alert"` too,
   * and two alerts mounting together are read one after the other with the
   * second interrupting the first — the same one-event-one-channel arithmetic
   * D11 applies to `announce()`. The member's own last action wins.
   */
  const probeFailed = !isSignedOut && error !== null && errorMessage === undefined;

  return (
    <div className="flex flex-col gap-4 py-8">
      {probeFailed && (
        <div className="mx-auto w-full max-w-sm">
          <Notice tone="bad" body={t('signedOut.probeFailed')} />
        </div>
      )}

      <SignInPanel
        mode={mode}
        onModeChange={(nextMode) => {
          // The panel announces this itself (D11) — see the file docstring.
          setMode(nextMode);
          setErrorMessage(undefined);
        }}
        onSubmit={(credentials) => {
          void submit(credentials);
        }}
        busy={busy}
        redirected={redirected}
        signUpOpen={SIGN_UP_OPEN}
        {...(errorMessage === undefined ? {} : { errorMessage })}
      />
    </div>
  );
}
